import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, nzd, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Stat, Loading } from "@/components/kit";

const REASONS = ["Expired", "Overproduction", "Spoilage", "Incorrect order", "Damaged", "Other"];

export default function Waste() {
  const { activeStore } = useAuth();
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [f, setF] = useState({ product_id: "", quantity: 1, reason: "Overproduction", cost: 0 });

  const load = useCallback(() => {
    if (!activeStore) return;
    setData(null);
    api.get("/waste", { params: { store_id: activeStore } }).then(({ data }) => setData(data));
    api.get("/products", { params: { store_id: activeStore } }).then(({ data }) => {
      setProducts(data);
      if (data[0]) setF((p) => p.product_id ? p : ({ ...p, product_id: data[0].id }));
    });
  }, [activeStore]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    await api.post("/waste", { store_id: activeStore, ...f, quantity: Number(f.quantity), cost: Number(f.cost) });
    toast.success("Waste recorded");
    setF({ ...f, quantity: 1, cost: 0 });
    load();
  };

  if (!data) return <Loading />;

  return (
    <div>
      <PageHeader title="Waste" subtitle="Record waste to cut over-ordering and feed the forecasting system" />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <Stat testid="waste-week" label="This week" value={nzd(data.week_cost)} accent="text-red-600" />
        <Stat testid="waste-month" label="This month" value={nzd(data.month_cost)} accent="text-red-600" />
        <Stat label="Top wasted" value={data.top_products[0]?.product || "—"} sub={data.top_products[0] ? nzd(data.top_products[0].cost) : ""} />
      </div>

      <Card className="p-5 mb-6" testid="waste-form">
        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <label className="block md:col-span-2"><span className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Product</span>
            <select data-testid="waste-product" value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })} className="mt-1 w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm">
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Quantity</span>
            <input type="number" data-testid="waste-qty" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} className="mt-1 w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm" /></label>
          <label className="block"><span className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Reason</span>
            <select value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="mt-1 w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm">
              {REASONS.map((r) => <option key={r}>{r}</option>)}
            </select></label>
          <div className="flex gap-2">
            <label className="block flex-1"><span className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Cost</span>
              <input type="number" step="0.01" data-testid="waste-cost" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} className="mt-1 w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm" /></label>
            <button data-testid="add-waste-btn" className="self-end bg-zinc-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-zinc-800 transition-colors duration-200"><Plus className="h-4 w-4" /></button>
          </div>
        </form>
      </Card>

      <Card className="overflow-hidden" testid="waste-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-[0.08em] text-zinc-400 border-b border-zinc-200">
            <th className="px-5 py-3 font-semibold">Product</th><th className="px-5 py-3 font-semibold text-right">Qty</th>
            <th className="px-5 py-3 font-semibold">Reason</th><th className="px-5 py-3 font-semibold text-right">Cost</th><th className="px-5 py-3 font-semibold">Date</th>
          </tr></thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-zinc-400"><Trash2 className="h-5 w-5 mx-auto mb-2" />No waste recorded yet</td></tr>}
            {data.items.map((w) => (
              <tr key={w.id} className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors duration-150">
                <td className="px-5 py-3 font-medium text-zinc-900">{w.product}</td>
                <td className="px-5 py-3 text-right tnum">{num(w.quantity, 1)}</td>
                <td className="px-5 py-3 text-zinc-500">{w.reason}</td>
                <td className="px-5 py-3 text-right tnum text-red-600">{nzd(w.cost)}</td>
                <td className="px-5 py-3 text-zinc-500">{w.day}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
